/**
 * DisplayFeatureManager - Central registry for display features.
 *
 * Responsibilities:
 * - Store all DisplayFeatures for the current case
 * - Emit events when features are added/removed/updated
 * - Provide query methods for features
 *
 * Does NOT:
 * - Know about viewports
 * - Create DisplayObjects
 * - Manage rendering
 *
 * @example
 * ```typescript
 * const manager = serviceManager.getService<DisplayFeatureManager>(
 *     DisplayFeatureManager.SERVICE_NAME
 * );
 *
 * // Add a feature (typically done by WorkflowService)
 * manager.addFeature({
 *     id: 'evar-centerlines',
 *     type: DisplayFeatureType.CENTERLINES,
 *     data: centerlines,
 *     properties: { tubeDiameter: 0.4; activeTubeDiameter: 0.8 },
 *     visible: true,
 * });
 *
 * // Subscribe to feature events (typically done by Viewports)
 * manager.subscribe(manager.EVENTS.FEATURE_ADDED, ({ feature }) => {
 *     // Create DisplayObject and render
 * });
 * ```
 */

import type {
    DisplayDataFor,
    DisplayFeatureType,
    DisplayPropertiesMap,
    FeatureAddedPayload,
    FeatureRemovedPayload,
    FeatureUpdatedPayload,
    FeatureVisibilityChangedPayload,
    IDisplayFeature,
} from './types';
import { ViewMode } from './types';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';

export class DisplayFeatureManager extends PubSubService {
    static readonly SERVICE_NAME = 'DisplayFeatureManager';

    static readonly REGISTRATION: ServiceRegistration = {
        name: DisplayFeatureManager.SERVICE_NAME,
        factory: (context: ServiceContext) => new DisplayFeatureManager(context),
    };

    readonly EVENTS = {
        /** Emitted when a new feature is registered */
        FEATURE_ADDED: 'display:feature:added',
        /** Emitted when a feature is removed */
        FEATURE_REMOVED: 'display:feature:removed',
        /** Emitted when feature properties are updated */
        FEATURE_UPDATED: 'display:feature:updated',
        /** Emitted when feature visibility changes */
        FEATURE_VISIBILITY_CHANGED: 'display:feature:visibility:changed',
    } as const;

    /** All registered features for current case */
    private features = new Map<string, IDisplayFeature>();

    constructor(_context: ServiceContext) {
        super();
    }

    /**
     * Register a new display feature.
     * Typically called by WorkflowService after loading and transforming data.
     *
     * Do nothing if a feature with the same id is already registered
     * @param feature - The feature to register
     */
    addFeature(feature: IDisplayFeature): void {
        if (this.features.has(feature.id)) {
            console.warn(`Feature "${feature.id}" is already registered`);
            return;
        }
        this.features.set(feature.id, feature);
        this.broadcast(this.EVENTS.FEATURE_ADDED, {
            feature,
        } satisfies FeatureAddedPayload);
    }

    /**
     * Remove a display feature.
     * Emits FEATURE_REMOVED event so viewports can clean up DisplayObjects.
     *
     * @param id - The feature ID to remove
     */
    removeFeature(id: string): void {
        const feature = this.features.get(id);
        if (feature) {
            this.features.delete(id);
            this.broadcast(this.EVENTS.FEATURE_REMOVED, {
                id,
                feature,
            } satisfies FeatureRemovedPayload);
        }
    }

    /**
     * Update feature visualization properties.
     * Emits FEATURE_UPDATED event so viewports can update rendering.
     *
     * @param id - The feature ID to update
     * @param properties - Properties to merge into existing properties
     */
    updateFeature<T extends DisplayFeatureType>(
        id: string,
        properties: Partial<DisplayPropertiesMap[T]>
    ): void {
        const feature = this.features.get(id);
        if (feature) {
            Object.assign(feature.properties, properties);
            this.broadcast(this.EVENTS.FEATURE_UPDATED, {
                id,
            } satisfies FeatureUpdatedPayload);
        }
    }

    /**
     * Replace feature domain data.
     * Emits FEATURE_UPDATED event so viewports can update rendering.
     *
     * Callers must specify T explicitly so the data type is checked:
     * `updateData<DisplayFeatureType.CURRENT_LOCATION>(id, location)`
     *
     * @param id - The feature ID to update
     * @param data - New data to assign
     */
    updateData<T extends DisplayFeatureType>(id: string, data: DisplayDataFor<T>): void {
        const feature = this.features.get(id) as IDisplayFeature<T> | undefined;
        if (feature) {
            feature.data = data;
            this.broadcast(this.EVENTS.FEATURE_UPDATED, {
                id,
            } satisfies FeatureUpdatedPayload);
        }
    }

    /**
     * Set per-view-mode visibility for a feature.
     * Emits FEATURE_VISIBILITY_CHANGED event if any mode changed.
     *
     * @param id - The feature ID to update
     * @param visibility - Map of ViewMode → boolean
     */
    setViewModeVisibility(id: string, visibility: ReadonlyMap<ViewMode, boolean>): void {
        const feature = this.features.get(id);
        if (!feature) return;

        let changed = false;
        for (const [mode, visible] of visibility) {
            if (feature.viewModeVisibility.get(mode) !== visible) {
                feature.viewModeVisibility.set(mode, visible);
                changed = true;
            }
        }

        if (changed) {
            this.broadcast(this.EVENTS.FEATURE_VISIBILITY_CHANGED, {
                id,
            } satisfies FeatureVisibilityChangedPayload);
        }
    }

    /**
     * Get a feature by ID.
     */
    getFeature(id: string): IDisplayFeature | undefined {
        return this.features.get(id);
    }

    /**
     * Get all features of a specific type.
     */
    getFeaturesByType(type: DisplayFeatureType): IDisplayFeature[] {
        return Array.from(this.features.values()).filter((f) => f.type === type);
    }

    /**
     * Get all features visible in the given view mode.
     */
    getFeaturesVisibleIn(viewMode: ViewMode): IDisplayFeature[] {
        return Array.from(this.features.values()).filter((f) => f.viewModeVisibility.get(viewMode) !== false);
    }

    /**
     * Get all registered features.
     */
    getAllFeatures(): IDisplayFeature[] {
        return Array.from(this.features.values());
    }

    /**
     * Check if a feature is registered.
     */
    hasFeature(id: string): boolean {
        return this.features.has(id);
    }

    /**
     * Get the number of registered features.
     */
    getFeatureCount(): number {
        return this.features.size;
    }

    /**
     * Reset clears all features when closing a case.
     */
    override reset(): void {
        console.log(`${DisplayFeatureManager.SERVICE_NAME} reset called`);
        this.features.clear();
        super.reset();
    }
}
